// Third-party
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import flattenArr from 'lodash/flatten';
import Dictionary from '@/types/dictionary';

/**
 * Take a nested json object and return a flat version of it
 * Example: {'foo': {'bar': value}} becomes {'foo.bar': 'value'}
 * @param obj Nested object to flatten
 * @param prefix Key prefix
 * @param separator Property separator
 */
export const flatten = (obj: Dictionary<any>, prefix: string = '', separator: string = '.') =>
  Object.keys(obj).reduce((flatObj: Dictionary<any>, key: string) => {
    const value = obj[key];
    const prefixedKey = prefix ? `${prefix}${separator}${key}` : key;

    if (typeof value === 'string') {
      flatObj[prefixedKey] = value;
    } else {
      Object.assign(flatObj, flatten(value, prefixedKey));
    }

    return flatObj;
  }, {});

/**
 * Take a nested array and return a flat version of it (single level deep)
 *
 * Example Arr: [['foo'], ['bar']] becomes ['foo', 'bar']
 * @param arr Array to flatten
 */
export const flattenArray = (arr: any[]) => flattenArr(arr);

/**
 * Creates a deep clone of an object or Array.
 * @param obj Object to clone
 * @returns Clone of the object
 */
export const deepClone = <T>(obj: T) => cloneDeep(obj) as T;

/**
 * Replaces an object in an array using the specified identifer,
 * adds it to the array otherwise
 * @param array Array containing the object to replace or add
 * @param obj Object to replace or add
 * @param {string} identifier  Key value to identify the object
 * @param {boolean} [unshift] add to the beginning of the array
 * @returns A shallow copy of the 'array' parameter, with the replaced or added object
 */
export const replaceById = (array: any[], obj: any, identifier: string = 'id', unshift?: boolean): any[] => {
  const temp = [...array];
  const index = temp.findIndex(p => p[identifier] === obj[identifier]);

  if (index === -1) {
    unshift ? temp.unshift(obj) : temp.push(obj);
  } else {
    temp[index] = obj;
  }

  return temp;
};

/**
 * Filters duplicate items in an array
 * @param array Array to filter
 * @returns An new array with only unique values
 */
export const uniqueArray = <T>(array: T[]) => Array.from(new Set(array));

/**
 * Sorts an array of objects from textual properties.
 * @param array Array to sort.
 * @param properties Array of properties to sort - first value is the field, second is the sort order (true = ascending, false = descending)
 * @returns A new sorted array.
 */
export const sortObjects = (array: unknown[], properties: [string, boolean?][]) => {
  const valueToString = (val: unknown): string => {
    if (val instanceof Date) {
      return val.getTime().toString();
    }

    if (typeof val === 'number') {
      return val.toString();
    }

    if (typeof val === 'string') {
      return val;
    }

    return '';
  };

  return array.slice().sort((a, b) => {
    for (const [field, ascending] of properties) {
      let first = a;
      let second = b;

      if (ascending === false) {
        first = b;
        second = a;
      }

      const valA = valueToString(get(first, field));
      const valB = valueToString(get(second, field));

      const compare = valA.toString().localeCompare(valB.toString());

      if (compare !== 0) {
        return compare;
      }
    }

    return 0;
  });
};

/**
 * Remove duplicate items from an array of objects by a property
 * @param {string} key the key to filter by
 * @param {Array<Dictionary<T>>} array the input array
 * @returns {Array<Dictionary<T>>} a new array with the duplicates removed
 */
export const removeDuplicates = <T>(array: Array<Dictionary<T>>, key: string): Array<Dictionary<T>> => {
  const lookup = new Set();
  return array.filter(obj => !lookup.has(obj[key]) && lookup.add(obj[key]));
};

/**
 * Returns the diff between two arrays by specific identifier or by the element itself
 * @param {Array} arr1 Old array
 * @param {Array} arr2 New array
 * @param {string} identifier  Key value to identify the object
 * @returns {Array} a new array the diff elements
 */
export const diffArray = (arr1: any[], arr2: any[], identifier?: string) => {
  if (identifier) {
    return arr1.filter(x => !arr2.some(y => y[identifier] === x[identifier]));
  }

  return arr1.filter(x => !arr2.includes(x));
};

/**
 * Enumerates an array, joining all elements with commas except the last one, where it's using
 * localized "and" instead. Ex: "item 1, item 2 and item 3"
 * @param array Array to be enumerated
 * @param lastSeparator Separator that should be used between the last 2 items. Defaults to 'and'.
 * @returns Enumerated array in string format.
 */
export const enumerateArray = <T>(array: T[], lastSeparator = 'and') => {
  if (array.length === 0) {
    return '';
  }

  if (array.length === 1) {
    return `${array[0]}`;
  }

  return `${array.slice(0, -1).join(', ')} ${lastSeparator} ${array.slice(-1)}`;
};

/**
 * Enumerates an array of JSX elements.
 * @param elements JSX elements to be enumerated
 * @param lastSeparator Separator that should be used between the last 2 items. Defaults to 'and'.
 * @returns Enumerated elements in JSX format.
 */
export const enumerateJSX = (elements: JSX.Element[], lastSeparator = 'and') => {
  const getSeparator = (index: number) => {
    if (index === elements.length - 1) {
      return '';
    }

    if (index === elements.length - 2) {
      return ` ${lastSeparator} `;
    }

    return ', ';
  };

  return elements.reduce((acc, element, index) => acc.concat(element, getSeparator(index)), [] as (JSX.Element | string)[]);
};

/**
 * Truncate a string with ellipsis. Should only be used in specific occasions - CSS's ellipsis should
 * always be prioritized.
 * @param str String to truncate.
 * @param maxChars Number of max chars.
 * @param ellipsis Ellipsis to be used. Defaults to '...'.
 * @returns Truncated string
 */
export const truncateString = (str: string, maxChars: number, ellipsis = '...') => {
  const trimmedStr = str.trim();

  if (trimmedStr.length <= maxChars + ellipsis.length) {
    return trimmedStr;
  }

  return `${trimmedStr.slice(0, maxChars).trim()}${ellipsis}`;
};

/**
 * Convert array to a queue with max length
 * @param {T[]} array input array
 * @param {T} item to add to the thee front of the array
 * @param {number} maxLength the length of the queue
 * @param {string} identifer the key of the item to replace
 * @returns a new array with the item at the front
 */
export const arrayAsQueue = <T>(array: T[], item: T, maxLength: number, identifer: string) => {
  const result = replaceById(array, item, identifer, true) as T[];
  result.splice(maxLength);
  return result;
};

/**
 * Get a key in an object by its value
 * @param {Dictionary<any>} object Object that contains key
 * @param {any} value Value to get key
 * @returns the key that contains the value
 */
export const getKeyByValue = (object: Dictionary<any>, value: any) => Object.keys(object).find(key => object[key] === value);

/**
 * Utility function to split an array in groups of given size. Doesn't affect original array
 * @param {any[]} array Array to be split
 * @param {number} groupSize Size of target groups
 * @returns {T[]} new array of smaller arrays based on groupSize
 */
export const splitArrayInGroups = <T>(array: T[], groupSize: number): T[][] => {
  if (!array.length) {
    return [];
  }
  if (array.length <= groupSize) {
    return [array];
  }

  const arrayCopy = array.slice();
  const groupedArray = arrayCopy.splice(0, groupSize);

  return [groupedArray].concat(splitArrayInGroups(arrayCopy, groupSize));
};
